import { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Clock, CheckCircle2, CalendarDays, Send } from 'lucide-react';
import { toast } from 'sonner';

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
];

const REASONS = [
  'Income Tax Filing',
  'GST Registration / Returns',
  'Professional Tax',
  'Company Registration',
  'Tax Audit',
  'Business Advisory',
  'Other',
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', date: '', time: '', reason: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const getMinDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error('Please fill in your name, phone, date and time');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/public/book-appointment', {
        name: form.name,
        phone: form.phone,
        email: form.email,
        date: form.date,
        time: form.time,
        reason: form.reason + (form.message ? ` - ${form.message}` : ''),
      });
      setSubmitted(true);
      toast.success('Appointment booked successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book appointment. Please call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative bg-[#1B2A1B] py-20">
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">Contact Us</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-semibold text-white tracking-tight mb-4">
            Get In Touch
          </h1>
          <p className="text-lg text-olive-300 max-w-2xl mx-auto">
            Book a free consultation or visit us at our Whitefield office. We're here to help with all your tax needs.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-[#111B11] tracking-tight mb-6">
                Office Details
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-sm bg-[#1B2A1B] flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111B11] mb-1">Visit Our Office</p>
                    <p className="text-sm text-[#3B5A3B] leading-relaxed">
                      SVT & Co., near Adhishakthi Royal Grove,<br />
                      Sector-1, 15th Cross, Opp BBMP Office,<br />
                      Honda Showroom, Varthur Main Rd,<br />
                      Ramagondanahalli, Whitefield,<br />
                      Bengaluru, Karnataka 560066, India
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-sm bg-[#1B2A1B] flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111B11] mb-1">Call Us</p>
                    <p className="text-sm text-[#3B5A3B]">+91 98765 43210</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-sm bg-[#1B2A1B] flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111B11] mb-1">Email Us</p>
                    <p className="text-sm text-[#3B5A3B]">info@svtandco.com</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-sm bg-[#1B2A1B] flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111B11] mb-1">Working Hours</p>
                    <p className="text-sm text-[#3B5A3B]">
                      Monday - Saturday: 9:00 AM - 6:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-sm overflow-hidden border border-[#E6EDE6] shadow-sm">
              <iframe
                title="SVT & Co Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.1!2d77.75!3d12.96!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDU3JzM2LjAiTiA3N8KwNDUnMDAuMCJF!5e0!3m2!1sen!2sin!4v1"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              />
            </div>
          </div>

          {/* Appointment Booking Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white border border-[#E6EDE6] rounded-sm p-12 text-center" data-testid="booking-success">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 size={32} className="text-green-600" />
                </div>
                <h2 className="font-heading text-3xl font-semibold text-[#111B11] mb-3">
                  Appointment Booked!
                </h2>
                <p className="text-sm text-olive-500 mb-2">
                  Thank you, <strong>{form.name}</strong>. Your appointment has been scheduled.
                </p>
                <p className="text-sm text-olive-500 mb-8">
                  <strong>{form.date}</strong> at <strong>{form.time}</strong>
                </p>
                <p className="text-xs text-olive-400 mb-6">
                  We'll confirm your appointment via phone. If you need to reschedule, please call us.
                </p>
                <Button
                  onClick={() => { setSubmitted(false); setForm({ name: '', phone: '', email: '', date: '', time: '', reason: '', message: '' }); }}
                  className="bg-[#1B2A1B] text-white hover:bg-[#2F472F] rounded-sm"
                  data-testid="book-another-btn"
                >
                  Book Another Appointment
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-[#E6EDE6] rounded-sm p-8" data-testid="booking-form">
                <div className="flex items-center gap-3 mb-6">
                  <CalendarDays size={24} className="text-[#D4AF37]" />
                  <div>
                    <h2 className="font-heading text-2xl font-semibold text-[#111B11]">Book an Appointment</h2>
                    <p className="text-xs text-olive-500">Free consultation - no obligation</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase font-semibold text-[#4F734F]">Full Name *</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        className="input-field h-11"
                        placeholder="Your full name"
                        required
                        data-testid="booking-name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase font-semibold text-[#4F734F]">Phone Number *</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="input-field h-11"
                        placeholder="+91 98765 43210"
                        required
                        data-testid="booking-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase font-semibold text-[#4F734F]">Email Address</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      className="input-field h-11"
                      placeholder="your@email.com"
                      data-testid="booking-email"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase font-semibold text-[#4F734F]">Preferred Date *</Label>
                      <Input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                        min={getMinDate()}
                        className="input-field h-11"
                        required
                        data-testid="booking-date"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase font-semibold text-[#4F734F]">Preferred Time *</Label>
                      <Select value={form.time || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, time: v === 'none' ? '' : v }))}>
                        <SelectTrigger className="input-field h-11" data-testid="booking-time">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" disabled>Select time</SelectItem>
                          {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase font-semibold text-[#4F734F]">Reason for Visit</Label>
                    <Select value={form.reason || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, reason: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="input-field h-11" data-testid="booking-reason">
                        <SelectValue placeholder="What do you need help with?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled>Select reason</SelectItem>
                        {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase font-semibold text-[#4F734F]">Additional Notes</Label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                      className="input-field min-h-[80px]"
                      placeholder="Any specific details about your tax needs..."
                      data-testid="booking-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-[#D4AF37] text-[#1B2A1B] hover:bg-[#C5A028] font-semibold rounded-sm gap-2 text-sm"
                    data-testid="submit-booking-btn"
                  >
                    {submitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-[#1B2A1B] border-t-transparent rounded-full animate-spin" />
                        Booking...
                      </div>
                    ) : (
                      <>
                        <Send size={16} /> Book Appointment
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-olive-400 text-center">
                    We'll call you to confirm the appointment within 24 hours.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
